import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, Menu, X } from 'lucide-react';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/#services' },
    { name: 'About', path: '/#about' },
    { name: 'Contact', path: '/contact' },
    { name: 'Free Strategy Call', path: '/free-strategy-call' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path);
  };

  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    if (path.includes('#')) {
      const [, hash] = path.split('#');
      if (location.pathname === '/') {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="header">
      {/* Top bar */}
      <div className="bg-slate-950 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-2">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-4 sm:gap-6">
              <a 
                href="tel:+18303270136" 
                className="flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors"
                data-testid="header-phone"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">(830) 327-0136</span>
              </a>
              <a 
                href="mailto:matthew@propainting.cloud" 
                className="flex items-center gap-2 text-slate-400 hover:text-amber-500 transition-colors"
                data-testid="header-email"
              >
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">matthew@propainting.cloud</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex justify-between items-center h-20 sm:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <div className="relative h-16 sm:h-20">
              <img 
                src="https://customer-assets.emergentagent.com/job_nbtx-painters/artifacts/lqy9nqx4_ProPainitngLoGo-removebg-preview.png" 
                alt="Pro Painting - 25 Years of Excellence - South Texas"
                className="h-full w-auto object-contain"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className={`font-body text-sm uppercase tracking-wider transition-colors ${
                  isActive(link.path) 
                    ? 'text-amber-500' 
                    : 'text-slate-300 hover:text-amber-500'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <Link
              to="/contact"
              className="glow-button bg-amber-500 text-black font-bold uppercase tracking-wider px-6 py-3 text-sm skew-button hover:bg-amber-400 transition-all"
              data-testid="header-cta"
            >
              <span>Get Free Estimate</span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-slate-950 border-t border-slate-800" data-testid="mobile-menu">
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => handleNavClick(link.path)}
                className={`font-body text-sm uppercase tracking-wider py-2 transition-colors ${
                  isActive(link.path) 
                    ? 'text-amber-500' 
                    : 'text-slate-300 hover:text-amber-500'
                }`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="glow-button bg-amber-500 text-black font-bold uppercase tracking-wider px-6 py-3 text-sm text-center hover:bg-amber-400 transition-all mt-2"
            >
              Get Free Estimate
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
